'use client';

import { c as _c } from "react/compiler-runtime";
import { type Context, createContext, type Dispatch, type PropsWithChildren, type ReactNode, type SetStateAction, useMemo, useState } from 'react';
type ContextValue<TContext> = {
  state: TContext | undefined;
  setState: Dispatch<SetStateAction<TContext | undefined>>;
};
type CacheContext<TContext> = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Provider: ({
    children
  }: PropsWithChildren) => ReactNode;
  context: Context<ContextValue<TContext> | undefined>;
};

/**
 * Utility function to create a context with a value and a setter for that value.
 *
 * @returns a context with a value and a setter for that value
 */
export function createCacheContext<TContext>(): CacheContext<TContext> {
  const context = createContext<ContextValue<TContext> | undefined>(undefined);
  function Provider(t0) {
    const $ = _c(5);
    const {
      children
    } = t0;
    const [state, setState] = useState(undefined);
    let t1;
    if ($[0] !== state) {
      t1 = {
        state,
        setState
      };
      $[0] = state;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    const returnValue = t1;
    let t2;
    if ($[2] !== children || $[3] !== returnValue) {
      t2 = <context.Provider value={returnValue}>{children}</context.Provider>;
      $[2] = children;
      $[3] = returnValue;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  return {
    Provider,
    context
  };
}